(() => {
  'use strict';
  if (customElements.get('bw-product-highlights')) return;
  const clean = value => String(value || '').replace(/\s+/g, ' ').trim();
  const key = value => clean(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const omit = /https?:|www\.|\$|\b(?:shipping|freight|checkout|coupon|discount|sale price|regular price|in stock|out of stock|warranty|returns?|refunds?|policy|credit|reviews?|subscribe|contact us|click|shopping|shop|browse|learn more|watch (?:the|this)|buy now|order now|we also|we offer|best on the market|one of the best)\b/i;
  const factual = /\d|\b(?:includes?|included|compatible|fits?|diameter|length|width|thickness|steel|birch|maple|walnut|aluminum|aluminium|brass|removable|replaceable|adjustable|dual ports|made in|stainless|finish|thread|mounting|shaft|quartz|battery|batteries|material|suitable|designed for)\b/i;
  function labelFor(value) {
    if (/\b(?:includes?|included|comes with|supplied with|contains)\b/i.test(value)) return 'Included';
    if (/\b(?:compatible|fits?|shaft|dial thickness)\b/i.test(value)) return 'Fit & compatibility';
    if (/\b(?:volts?|watts?|120v|220v|power|battery|batteries)\b/i.test(value)) return 'Power';
    if (/\b(?:diameter|length|width|thickness|dimensions?|sizes?)\b/i.test(value)) return 'Dimensions';
    if (/\b(?:made in|manufactured in)\b/i.test(value)) return 'Made in';
    if (/\b(?:material|stainless|solid birch|solid maple|solid walnut|zinc.plated|brass.plated|cotton|polyester|fabric)\b/i.test(value)) return 'Material & finish';
    if (/\b(?:sourced from)\b/i.test(value)) return 'Origin';
    if (/\b(?:finish|water.repell|water.resist)\b/i.test(value)) return 'Finish';
    if (/\b(?:absorb|apply|application|coat|drying)\b/i.test(value)) return 'Application';
    if (/\b(?:suitable|designed for|used for)\b/i.test(value)) return 'Use';
    if (/\b(?:feed rate|teeth|tpi|cuts?|cutting)\b/i.test(value)) return 'Cutting';
    if (/\b(?:grit|abrasive)\b/i.test(value)) return 'Grit';
    if (/\b(?:pocket|hood|drawstrings|cuffed|garment)\b/i.test(value)) return 'Features';
    return 'Details';
  }
  class ProductHighlights extends HTMLElement {
    static candidates(product) {
      const rows = [];
      const seen = new Set();
      const existing = [product.title, product.short].map(key).filter(Boolean);
      const add = (label, value, source, priority = 0) => {
        label = clean(label).replace(/[:\s]+$/, ''); value = clean(value);
        if (!value || value.length < 3 || value.length > 180 || omit.test(value)) return;
        const normalized = key(value);
        if (!normalized || seen.has(normalized) || existing.includes(normalized)) return;
        if (rows.some(row => key(row.value).includes(normalized) || normalized.includes(key(row.value)))) return;
        seen.add(normalized);
        rows.push({ label: label.length > 36 ? labelFor(value) : label || labelFor(value), value, source, priority });
      };
      // Retain the already reviewed wording for these kits; all other products use their own source content.
      if (/^wood-burning-(?:deluxe-superpro-burner-kit-with-9|superpro-burner-kit-with-5)-removable-tips-by-colwood-usa$/.test(product.handle || '')) {
        const count = product.handle.includes('with-9-') ? 9 : 5;
        add('In the kit', `${count} replaceable tips and 2 handpieces`, 'reviewed-description', 100);
        add('Ready to work', 'EZ Tip Puller, cleaning cloth and honing paper', 'reviewed-description', 100);
        add('Power', '120V control unit · 46 watts', 'reviewed-description', 100);
        return rows;
      }
      const read = (html, source) => {
        const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
        doc.querySelectorAll('script,style,noscript,iframe,form,button').forEach(el => el.remove());
        doc.querySelectorAll('br').forEach(el => el.replaceWith(doc.createTextNode('\n')));
        doc.querySelectorAll('tr').forEach(tr => {
          const cells = Array.from(tr.querySelectorAll('th,td')).map(el => clean(el.textContent)).filter(Boolean);
          if (cells.length === 2 && cells[0].length <= 36) add(cells[0], cells[1], source, 35);
        });
        doc.querySelectorAll('dt').forEach(dt => { if (dt.nextElementSibling?.tagName === 'DD') add(dt.textContent, dt.nextElementSibling.textContent, source, 35); });
        const excerpts = text => {
          text = clean(text);
          if (text.length <= 180) return [text];
          return text.split(/(?<=[.!?])\s+(?=[A-Z0-9])/).map(clean).filter(value => value.length <= 180);
        };
        doc.querySelectorAll('li').forEach(li => {
          if (li.querySelector('ul,ol')) return;
          excerpts(li.textContent).filter(value => factual.test(value)).forEach(value => {
            const pair = value.match(/^([^:]{2,34}):\s*(.{7,})$/);
            add(pair ? pair[1] : labelFor(value), pair ? pair[2] : value, source, source === 'specs' ? 25 : 20);
          });
        });
        doc.querySelectorAll('p').forEach(p => {
          if (p.closest('li,td,th')) return;
          excerpts(p.textContent).filter(value => factual.test(value)).forEach(value => add(labelFor(value), value, source, source === 'specs' ? 15 : 10));
        });
        // Some imported descriptions use literal bullets and line breaks instead of HTML lists.
        if (doc.body.textContent.includes('•')) doc.body.textContent.split('•').slice(1).forEach(text => excerpts(text).forEach(value => add(labelFor(value), value, source, 20)));
      };
      read(product.specsHtml, 'specs'); read(product.descriptionHtml, 'description');
      const title = clean(product.title);
      const dimension = title.match(/(?<![\w./])(?:\d+(?:-\d+\/\d+|\.\d+|\/\d+)?|\.\d+)\s*(?:["”]|inches?\b|mm\b|cm\b|tpi\b)?(?![\w./])(?:\s*[x×]\s*(?:\d+(?:-\d+\/\d+|\.\d+|\/\d+)?|\.\d+)\s*(?:["”]|inches?\b|mm\b|cm\b|tpi\b)?(?![\w./])){1,3}/i);
      if (dimension) add(/tpi/i.test(dimension[0]) ? 'Blade profile' : 'Dimensions', dimension[0], 'title', 30);
      const pack = title.match(/\b(?:Per|Pack of|Pk\/|Pk of|Set of)\s*\d+(?:\s*(?:pieces|screws|tips|dozen))?/i);
      if (pack) add('Pack size', pack[0], 'title', 30);
      const finish = title.match(/\b(?:Zinc Plated|Brass Plated|Black Oxide|Stainless Steel)\b/i);
      if (finish) add('Material & finish', finish[0], 'title', 30);
      if (!rows.length && clean(product.productType)) add('Category', clean(product.productType).split('|').pop(), 'product-type', -10);
      if (!rows.length) { const doc = new DOMParser().parseFromString(String(product.descriptionHtml || ''), 'text/html'); doc.querySelectorAll('script,style,noscript,iframe').forEach(el => el.remove()); add('About this item', doc.body.textContent, 'description', -20); }
      return rows.sort((a, b) => b.priority - a.priority).slice(0, 12);
    }
    connectedCallback() {
      this.controller?.abort(); this.resizeObserver?.disconnect(); this.mutationObserver?.disconnect();
      this.controller = new AbortController();
      const section = this.closest('[data-section-type="product"]');
      const galleryItem = section?.querySelector('.product-block-list__item--gallery');
      const infoItem = this.closest('.product-block-list__item--info');
      // Measure the visible cards; legacy theme sizing can stretch the outer grid items.
      this.gallery = galleryItem?.querySelector(':scope > .card') || galleryItem;
      this.info = infoItem?.querySelector(':scope > .card') || infoItem;
      this.hidden = true;
      if (!this.gallery || !this.info || !document.body.classList.contains('bw-simple-product')) return;
      const product = { title: this.dataset.title, handle: this.dataset.handle, short: this.dataset.short, productType: this.dataset.productType, descriptionHtml: this.dataset.description, specsHtml: this.dataset.specs };
      const candidates = ProductHighlights.candidates(product);
      const chosen = [];
      for (const row of candidates) {
        // Prefer three different kinds of information over three repetitions of the same label.
        if (!chosen.some(other => other.label === row.label)) chosen.push(row);
        if (chosen.length === 3) break;
      }
      for (const row of candidates) { if (chosen.length === 3) break; if (!chosen.includes(row)) chosen.push(row); }
      this.replaceChildren(); this.rows = [];
      if (!chosen.length) { this.dataset.available = '0'; return; }
      const heading = document.createElement('h2'); heading.textContent = 'At a glance';
      const list = document.createElement('dl');
      chosen.forEach(row => { const wrapper = document.createElement('div'), term = document.createElement('dt'), detail = document.createElement('dd'); term.textContent = row.label; detail.textContent = row.value; wrapper.append(term, detail); wrapper.hidden = true; list.append(wrapper); this.rows.push(wrapper); });
      this.append(heading, list); this.dataset.available = String(chosen.length);
      this.media = window.matchMedia('(min-width:1000px)');
      this.schedule = () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.fit()); };
      window.addEventListener('resize', this.schedule, { signal: this.controller.signal });
      this.media.addEventListener('change', this.schedule, { signal: this.controller.signal });
      this.resizeObserver = new ResizeObserver(this.schedule); this.resizeObserver.observe(this.gallery); this.resizeObserver.observe(this.info);
      this.mutationObserver = new MutationObserver(records => { if (records.some(record => !this.contains(record.target))) this.schedule(); });
      this.mutationObserver.observe(this.info, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'hidden'] });
      document.fonts?.ready.then(() => { if (this.isConnected) this.schedule(); });
      this.schedule();
    }
    fit() {
      this.hidden = true; this.dataset.visible = '0';
      if (!this.media?.matches || !this.isConnected || !this.rows?.length || this.gallery.getBoundingClientRect().width === 0) return;
      const galleryHeight = this.gallery.getBoundingClientRect().height;
      // Card heights remain stable when the theme's sticky purchase panel moves during scrolling.
      if (galleryHeight - this.info.getBoundingClientRect().height < 112) return;
      this.rows.forEach(row => { row.hidden = true; });
      this.hidden = false; this.style.visibility = 'hidden';
      let count = 0;
      for (const row of this.rows) {
        row.hidden = false;
        if (this.info.getBoundingClientRect().height > galleryHeight - 12) { row.hidden = true; break; }
        count++;
      }
      this.hidden = count === 0; this.dataset.visible = String(count); this.style.removeProperty('visibility');
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); this.mutationObserver?.disconnect(); cancelAnimationFrame(this.frame); }
  }
  customElements.define('bw-product-highlights', ProductHighlights);
})();
